import { FormBlock, FormCard } from '@qbe/ads-blocks-forms';
import { Box, Button, Spinner, Typography } from '@qbe/ads-core';
import React, { useEffect, useState } from 'react';

import ExcessErrorModal from '~/components/ExcessErrorModal';
import GrossIncome from '~/components/GrossIncome';
import IndemnityPeriod from '~/components/IndemnityPeriod';
import { useAppContent, useAppDispatch, useCoverDetails, useQuote } from '~/hooks';
import { useAppNavigate } from '~/hooks/useAppNavigate';
import { useCoverDetailsEffects } from '~/hooks/useCoverDetailsEffects';
import { useTrackOptimizeActivate } from '~/hooks/useTrackOptimizeActivate';
import { addValidatedCover, removeValidatedCover } from '~/slices/selectCover';
import { qalFormCompleteAction } from '~/thunks/analytics';
import { Cover } from '~/types/cover';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';

const CoverDetailsBusinessInterruption: React.FC = () => {
  const { getTextContent: getSharedContent } = useAppContent({ basePath: 'shared' });
  const { getTextContent, getJSXContent } = useAppContent();
  const { getTextContent: getBiText } = useAppContent({
    basePath: 'dsme.coverDetailsBusinessInterruption',
  });
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { levelOfExcess } = useQuote();
  const { isValidBusinessInterruption, grossIncome } = useCoverDetails();
  const [openExcessError, setOpenExcessError] = useState(false);

  const { quotePremiums, isLoading, buttonText } = useCoverDetailsEffects({
    cover: Cover.BusinessInterruption,
  });

  const handleFormComplete = () => {
    if (isFormStartStarted()) dispatch(qalFormCompleteAction());
  };

  const handleNext = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    handleFormComplete();
    grossIncome! < levelOfExcess! ? setOpenExcessError(true) : quotePremiums();
  };

  useEffect(() => {
    !isValidBusinessInterruption
      ? dispatch(removeValidatedCover(Cover.BusinessInterruption))
      : dispatch(addValidatedCover(Cover.BusinessInterruption));
  }, [dispatch, isValidBusinessInterruption]);

  // Tracks the "optimize.activate" event when the component mounts or re-renders.
  // This is used to notify Webtrends (or other analytics tools) for visibility tracking
  // and ensures dynamic content is properly handled.
  useTrackOptimizeActivate();

  return (
    <>
      <FormCard variant="sectionTitle" title={getBiText('title')}>
        <Spinner description={getSharedContent('loadingLabel')} loading={isLoading} />

        <FormBlock preset="text_questionDesc" paddingBottom="md">
          <Typography textAlign="center">{getBiText('description')}</Typography>
        </FormBlock>

        <Box data-track="Indemnity period" data-track-value="capture">
          <FormBlock preset="toggle">
            <IndemnityPeriod />
          </FormBlock>
        </Box>

        <GrossIncome />

        <FormBlock preset="button">
          <Button
            data-track={`${buttonText} - business interruption`}
            onClick={handleNext}
            disabled={!isValidBusinessInterruption || isLoading}
            label={buttonText}
          />
        </FormBlock>
      </FormCard>

      <ExcessErrorModal
        open={openExcessError}
        onContinue={() => {
          setOpenExcessError(false);
          navigate('#grossIncome');
        }}
        onCancel={() => {
          navigate(getTextContent('shared.qbeHome'));
        }}
      >
        <Typography>{getJSXContent('dsme.coverDetails.shared.excessError.description')}</Typography>
      </ExcessErrorModal>
    </>
  );
};

export default CoverDetailsBusinessInterruption;
